import { ProductItem } from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import { productToPriceComponents } from 'lane-shared/domains/billingPayments/helpers';
import { getPricing } from 'lane-shared/domains/productCatalog/helpers';

/**
 * Create an updated product item with recalculated pricing
 * after a rate override
 */
export const createUpdatedProductItem = (
  item: ProductItem,
  newAmount: number,
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  userInputRate?: number
): ProductItem => {
  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  // Build/update originalPrice based on the scenario
  let originalPrice = item.product.originalPrice;

  if (userInputRate !== undefined && !item.isRateOverridden) {
    if (!originalPrice || item.product.sellingPrice?.listPrice !== undefined) {
      // Scenario 1: No originalPrice - capture current pricing as original
      // Scenario 2: Has originalPrice + sellingPrice (price exception) - update to reflect exception
      originalPrice = {
        amount: item.product.amount,
        markup: item.product.markup || undefined,
        tax: item.product.tax || undefined,
      };
    }
  }

  const updatedProduct = {
    ...item.product,
    amount: newAmount,
    // Remove old sellingPrice to force recalculation
    sellingPrice: undefined,
    // Set the original price (either existing or newly captured)
    originalPrice,
  };

  // Create price components for recalculation
  const priceComponents = productToPriceComponents(updatedProduct);
  const { grossMarkup, grossTax, listPrice, netPrice } = getPricing({
    currency,
    locale,
    priceComponents,
  });

  // Round all pricing values to ensure proper decimal precision
  const roundedPricing = {
    grossTax: roundToTwoDecimals(grossTax),
    grossMarkup: roundToTwoDecimals(grossMarkup),
    listPrice: roundToTwoDecimals(listPrice),
    netPrice: roundToTwoDecimals(netPrice),
  };

  return {
    ...item,
    product: {
      ...updatedProduct,
      amount: roundToTwoDecimals(newAmount),
      sellingPrice: roundedPricing,
    },
    isRateOverridden: true,
    // Store the user's original input rate for validation
    userInputRate: userInputRate ?? roundedPricing.listPrice,
  };
};
